import { useState } from "react";
import api, { API } from "@/lib/api";
import { chunkedUpload, uploadError, MAX_UPLOAD } from "@/lib/chunkedUpload";
import { UploadProgress } from "@/components/undertake/UploadProgress";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";
import { Gate, ready, Panel, useLoad, stamp, cad, Chip, Table, Filters, Modal, fieldStyle, Label } from "@/components/console/ui";
import { openFile } from "@/components/undertake/ReviewTab";

function PostUndertaking({ onDone }) {
  const [text, setText] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const ok = text.trim() && n >= 1;
  const submit = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try {
      await api.post("/undertakings", { text, provisions: n });
      toast.success("undertaking posted to the pile");
      setText(""); setAmount("");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="post an undertaking · infinite provisions" testId="post-undertaking">
      <Label>task · {countWords(text)}/77 words</Label>
      <textarea data-testid="post-ut-text" value={text} onChange={(e) => setText(limitWords(e.target.value, text, 77))} rows={5} className="w-full p-3 text-sm resize-none mb-4" style={fieldStyle} />
      <Label>reward · provisions</Label>
      <input data-testid="post-ut-provisions" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" className="w-full px-3 py-2 text-sm" style={fieldStyle} />
      <button onClick={submit} disabled={!ok || busy} data-testid="post-ut-submit" className="btn w-full py-3 mt-4 text-xs tracking-widest">{busy ? "…" : "post to the pile"}</button>
    </Panel>
  );
}

function BraveForm({ u, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/undertakings/${u.id}/flag`, { category: "brave", appreciation: text }); toast.success("sent to only the brave"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="mt-6 pt-5" style={{ borderTop: "1px solid var(--border)" }}>
      <Label>send to only the brave</Label>
      <textarea data-testid="brave-appreciation" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="why is this brave…" className="w-full p-3 text-sm resize-none" style={fieldStyle} />
      <button onClick={submit} disabled={busy} data-testid="brave-submit" className="btn w-full py-3 mt-3 text-xs tracking-widest">{busy ? "…" : "feature"}</button>
    </div>
  );
}

function UndertakingDetail({ u, onClose, onChanged }) {
  return (
    <Modal title="undertaking" onClose={onClose} testId="undertaking-detail" wide>
      <div className="flex flex-wrap gap-3 items-center text-xs mb-3"><Chip on>{(u.status || "unknown").replace("_", " ")}</Chip><span>{u.provisions} provisions</span><span className="opacity-60">posted {stamp(u.created_at)}</span>{u.fail_count > 0 && <span>failed {u.fail_count}×</span>}</div>
      <p className="long-text-sm text-sm leading-relaxed">{u.text}</p>
      <div className="grid grid-cols-2 gap-2 text-xs mt-4">
        <span>delegator · <span style={{ color: "var(--accent)" }}>{u.delegator}</span></span>
        <span>undertaker · <span style={{ color: "var(--accent)" }}>{u.undertaker || "—"}</span></span>
        {u.deadline && <span>deadline · {stamp(u.deadline)}</span>}
        {u.completed_at && <span>completed · {stamp(u.completed_at)}</span>}
      </div>
      {u.files.length > 0 && <div className="mt-4"><Label>proof files</Label><div className="flex flex-wrap gap-2">{u.files.map((f) => <button key={f.id} onClick={() => openFile(u.id, f)} data-testid={`ut-file-${f.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">↓ {f.name || "file"}</button>)}</div></div>}
      {u.status === "completed" && (u.flagged ? <p className="text-xs mt-6" style={{ color: "var(--accent)" }}>featured on only the brave</p> : <BraveForm u={u} onDone={() => { onChanged(); onClose(); }} />)}
    </Modal>
  );
}

export function Undertakings() {
  const [status, setStatus] = useState("");
  const [rows, reload] = useLoad(`/console/undertakings?status=${status}`, 15000);
  const [open, setOpen] = useState(null);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
      <Panel className="xl:col-span-8" title="all undertakings" testId="console-undertakings" right={<Filters testId="ut-filter" value={status} onChange={setStatus} options={[["", "all"], ["open", "open"], ["undertaken", "in progress"], ["sent_up", "verify"], ["completed", "completed"], ["failed", "failed"]]} />}>
        <Table testId="ut-table" rows={rows} onRow={setOpen} empty="no undertakings"
          cols={[
            { k: "created_at", label: "posted", render: (r) => stamp(r.created_at), cls: "whitespace-nowrap" },
            { k: "text", label: "challenge", render: (r) => <span className="line-clamp-2">{r.text}</span> },
            { k: "delegator", label: "delegator" },
            { k: "undertaker", label: "undertaker", render: (r) => r.undertaker || "—" },
            { k: "provisions", label: "reward" },
            { k: "status", label: "status", render: (r) => <div className="flex flex-col gap-1 items-start"><Chip on={r.status === "sent_up"}>{(r.status || "unknown").replace("_", " ")}</Chip>{r.flagged && <Chip>brave</Chip>}</div> },
          ]} />
      </Panel>
      <div className="xl:col-span-4"><PostUndertaking onDone={reload} /></div>
      {open && <UndertakingDetail u={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}

function FlagList({ category, title }) {
  const [rows, reload] = useLoad(`/flags?category=${category}`);
  const [open, setOpen] = useState(null);
  const remove = async (f) => {
    if (!window.confirm(`remove ${f.target_user_name || "this entry"} from ${title}?`)) return;
    try { await api.delete(`/flags/${f.id}`); toast.success("removed"); setOpen(null); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Panel title={title} testId={`list-${category}`} right={<span className="text-xs">{ready(rows) ? rows.length : "…"} featured</span>}>
      <Table testId={`list-${category}-table`} rows={rows} onRow={setOpen} empty="nobody featured yet"
        cols={[
          { k: "target_user_name", label: "user", render: (f) => <span style={{ color: "var(--accent)" }}>{f.target_user_name || "—"}</span> },
          { k: "provisions", label: category === "genius" ? "awarded" : "earned", render: (f) => `+${f.provisions ?? 0}` },
          { k: "created_at", label: "when", render: (f) => stamp(f.created_at) },
        ]} />
      {open && (
        <Modal title={`${title} · ${open.target_user_name || "—"}`} onClose={() => setOpen(null)} testId="flag-entry">
          <Label>{category === "genius" ? "the ask" : "the undertaking"}</Label>
          <p className="long-text-sm text-sm leading-relaxed mb-4">{open.ask_text || open.undertaking_text || "—"}</p>
          <Label>member note</Label>
          <p className="text-sm mb-4">{open.appreciation || "—"}</p>
          <button onClick={() => remove(open)} data-testid="flag-entry-remove" className="btn w-full py-3 text-xs tracking-widest">remove from list</button>
        </Modal>
      )}
    </Panel>
  );
}

export function Lists() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <FlagList category="genius" title="remarkable minds" />
      <FlagList category="brave" title="only the brave" />
    </div>
  );
}

function DonationVideo({ d }) {
  if (!d.video) return <Chip on>no video</Chip>;
  return <a href={`${API}/foundation/donations/${d.id}/video`} target="_blank" rel="noreferrer" data-testid={`donation-play-${d.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">▶ play</a>;
}

export function Donations() {
  const [rows, reload] = useLoad("/console/donations");
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), amount_cad: "" });
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: k === "amount_cad" ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value });
  const ok = f.date && Number(f.amount_cad) > 0 && file;
  const add = async () => {
    if (!ok || prog) return;
    if (file.size > MAX_UPLOAD) { toast.error("video must be under 500 MB"); return; }
    setProg({ p: 0, phase: "uploading" });
    let created = null;
    try {
      const { data } = await api.post("/console/donations", { ...f, amount_cad: Number(f.amount_cad) });
      created = data;
      await chunkedUpload({ target: "donation", targetId: data.id, file, onProgress: (p, phase) => setProg({ p, phase }) });
      toast.success("donation video posted publicly");
      setF({ ...f, amount_cad: "" }); setFile(null);
    } catch (err) {
      toast.error(uploadError(err));
      if (created) await api.delete(`/console/donations/${created.id}`).catch(() => {});
    } finally { setProg(null); reload(); }
  };
  const del = async (d) => {
    if (!window.confirm("remove this donation from the public list?")) return;
    try { await api.delete(`/console/donations/${d.id}`); toast.success("removed"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const total = (ready(rows) ? rows : []).filter((d) => d.video).reduce((a, d) => a + d.amount_cad, 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <Panel className="lg:col-span-4" title="post a foundation donation" testId="donation-form">
        <Label>date</Label><input type="date" data-testid="donation-date" value={f.date} onChange={set("date")} className="w-full px-3 py-2 text-sm mb-4" style={{ ...fieldStyle, colorScheme: "dark" }} />
        <Label>amount · cad</Label><input data-testid="donation-amount" value={f.amount_cad} onChange={set("amount_cad")} placeholder="0.00" className="w-full px-3 py-2 text-sm mb-4" style={fieldStyle} />
        <Label>video · up to 500 mb</Label>
        <label className="btn w-full py-3 text-xs tracking-widest flex justify-center cursor-pointer" data-testid="donation-video-pick">
          <span className="truncate px-2">{file ? file.name : "choose video"}</span>
          <input type="file" accept="video/*" className="hidden" data-testid="donation-video-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        {prog && <UploadProgress items={{ one: { name: file?.name, ...prog } }} />}
        <button onClick={add} disabled={!ok || !!prog} data-testid="donation-add" className="btn w-full py-3 mt-4 text-xs tracking-widest">{prog ? "uploading…" : "post to public list"}</button>
        <p className="text-[10px] opacity-60 mt-3">keep this page open while uploading · this does not change the pot · record the payout under sentence &amp; pot</p>
      </Panel>
      <Panel className="lg:col-span-8" title="public donations list" testId="donation-list" right={<span className="text-xs" data-testid="donation-total">{cad(total)} cad total</span>}>
        <Table testId="donation-table" rows={rows} empty="no donations yet · the landing page shows “no donations yet”"
          cols={[
            { k: "date", label: "date" },
            { k: "amount_cad", label: "cad", render: (d) => <span style={{ color: "var(--accent)" }}>{cad(d.amount_cad)}</span> },
            { k: "video", label: "video", render: (d) => <DonationVideo d={d} /> },
            { k: "size", label: "size", render: (d) => (d.video ? `${Math.max(0.1, d.video.size / 1048576).toFixed(1)} mb` : "—") },
            { k: "x", label: "", render: (d) => <button onClick={() => del(d)} data-testid={`donation-delete-${d.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">remove</button> },
          ]} />
      </Panel>
    </div>
  );
}
